import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import { useAuth } from './AuthContext';
import LegalDisclaimer from './LegalDisclaimer';

// Initialize Mermaid with larger fonts
mermaid.initialize({
  startOnLoad: true,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  themeVariables: {
    fontSize: '30px',
    primaryTextColor: '#1f2937',
    primaryColor: '#e3f2fd',
    primaryBorderColor: '#2196f3',
    edgeLabelBackground: '#ffffff'
  },
  flowchart: {
    htmlLabels: true,
    curve: 'basis',
    padding: 30,
    nodeSpacing: 110,
    rankSpacing: 110
  }
});

function LegalQA() {
  const { user, isAdmin } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [myQuestions, setMyQuestions] = useState([]);
  const [publicQuestions, setPublicQuestions] = useState([]);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isDeepLink, setIsDeepLink] = useState(false);
  const [upvoting, setUpvoting] = useState(false);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [threadQAs, setThreadQAs] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [selectedFoundation, setSelectedFoundation] = useState(null);
  const [showFoundationModal, setShowFoundationModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessingDiagram, setReprocessingDiagram] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [diagramView, setDiagramView] = useState('hierarchy'); // 'hierarchy' or 'timeline'
  const [timelineDiagram, setTimelineDiagram] = useState(null);
  const [timelineFoundationMap, setTimelineFoundationMap] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [showFullScreenDiagram, setShowFullScreenDiagram] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const mermaidRef = useRef(null);
  const fullScreenMermaidRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const answerDataRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const diagramViewRef = useRef('hierarchy');
  const timelineFoundationMapRef = useRef(null);

  // Keep answerDataRef in sync with answer state
  useEffect(() => {
    answerDataRef.current = answer;
  }, [answer]);

  // Keep diagramViewRef in sync with diagramView state
  useEffect(() => {
    diagramViewRef.current = diagramView;
  }, [diagramView]);

  // Keep timelineFoundationMapRef in sync with timelineFoundationMap state
  useEffect(() => {
    timelineFoundationMapRef.current = timelineFoundationMap;
  }, [timelineFoundationMap]);

  // Define global callback for Mermaid click events
  useEffect(() => {
    window.handleFoundationClick = (nodeId) => {
      console.log('handleFoundationClick called with nodeId:', nodeId);

      // Use refs to get current values (not closure values)
      const currentDiagramView = diagramViewRef.current;
      const currentTimelineFoundationMap = timelineFoundationMapRef.current;
      const currentAnswer = answerDataRef.current;

      console.log('Current diagram view:', currentDiagramView);

      // Use timeline foundation map if in timeline view, otherwise use hierarchy map
      let foundationMap;
      if (currentDiagramView === 'timeline' && currentTimelineFoundationMap) {
        console.log('Using timeline foundation map');
        foundationMap = currentTimelineFoundationMap;
      } else if (currentAnswer?.foundationMap) {
        console.log('Using hierarchy foundation map from answer');
        foundationMap = currentAnswer.foundationMap;
      } else if (currentAnswer?.clarityAnalysis?.foundationMap) {
        console.log('Using hierarchy foundation map from clarityAnalysis');
        foundationMap = currentAnswer.clarityAnalysis.foundationMap;
      }

      if (!foundationMap) {
        console.log('No foundation map available');
        return;
      }

      const foundation = foundationMap.foundations?.find(
        f => f.mermaidNodeId === nodeId
      );

      if (foundation) {
        console.log('Found foundation:', foundation.name);
        setSelectedFoundation(foundation);
        setShowFoundationModal(true);
      } else {
        console.log('No foundation found for nodeId:', nodeId);
      }
    };

    return () => {
      delete window.handleFoundationClick;
    };
  }, []);

  useEffect(() => {
    // Fetch recent Q&A history
    fetchRecentHistory();
    fetchMyQuestions();
    fetchPublicQuestions();

    // Check for Q&A ID in URL
    if (id) {
      loadQAById(id);
    }
  }, [id]);

  // Clear cached timeline diagram and foundation map when answer changes
  useEffect(() => {
    setTimelineDiagram(null);
    setTimelineFoundationMap(null);
  }, [answer?.qaId]);

  // Fetch timeline diagram when timeline view is selected
  useEffect(() => {
    if (diagramView === 'timeline' && answer && answer.qaId && !timelineDiagram && !loadingTimeline) {
      const fetchTimelineDiagram = async () => {
        setLoadingTimeline(true);
        try {
          const response = await fetch(`/api/qa/${answer.qaId}/timeline-diagram`, {
            credentials: 'include'
          });
          const data = await response.json();
          if (data.success && data.diagram) {
            setTimelineDiagram(data.diagram);
            setTimelineFoundationMap(data.updatedFoundationMap);
          }
        } catch (error) {
          console.error('Error fetching timeline diagram:', error);
        } finally {
          setLoadingTimeline(false);
        }
      };
      fetchTimelineDiagram();
    }
  }, [diagramView, answer, timelineDiagram, loadingTimeline]);

  // Render Mermaid diagram (click handlers set via Mermaid's built-in callback)
  useEffect(() => {
    if (answer && mermaidRef.current) {
      try {
        console.log('Rendering Mermaid diagram with foundation map...');

        let diagramToRender = null;

        // Choose the appropriate diagram based on view
        if (diagramView === 'timeline' && timelineDiagram) {
          diagramToRender = timelineDiagram;
        } else if (diagramView === 'hierarchy' && answer.legalDiagram) {
          diagramToRender = answer.legalDiagram;
        }

        if (!diagramToRender) {
          return;
        }

        // Clear previous content
        mermaidRef.current.innerHTML = '';

        // Create a new div for the diagram
        const diagramDiv = document.createElement('div');
        diagramDiv.className = 'mermaid';
        diagramDiv.textContent = diagramToRender;
        mermaidRef.current.appendChild(diagramDiv);

        // Re-initialize Mermaid to render the new diagram
        mermaid.init(undefined, diagramDiv);

        console.log('✓ Mermaid diagram rendered. Click handlers set via global callback.');
      } catch (error) {
        console.error('Error rendering Mermaid diagram:', error);
      }
    }
  }, [answer, diagramView, timelineDiagram]);

  // Render full-screen Mermaid diagram
  useEffect(() => {
    if (showFullScreenDiagram && answer && fullScreenMermaidRef.current) {
      try {
        console.log('Rendering full-screen Mermaid diagram...');

        let diagramToRender = null;

        // Choose the appropriate diagram based on view
        if (diagramView === 'timeline' && timelineDiagram) {
          diagramToRender = timelineDiagram;
        } else if (diagramView === 'hierarchy' && answer.legalDiagram) {
          diagramToRender = answer.legalDiagram;
        }

        if (!diagramToRender) {
          return;
        }

        // Clear previous content
        fullScreenMermaidRef.current.innerHTML = '';

        // Create a new div for the diagram
        const diagramDiv = document.createElement('div');
        diagramDiv.className = 'mermaid';
        diagramDiv.textContent = diagramToRender;
        fullScreenMermaidRef.current.appendChild(diagramDiv);

        // Re-initialize Mermaid to render the new diagram
        mermaid.init(undefined, diagramDiv);

        // Add manual click handlers as backup after a short delay to ensure SVG is rendered
        setTimeout(() => {
          const svg = fullScreenMermaidRef.current?.querySelector('svg');
          if (svg) {
            // Find all node groups (Mermaid creates <g class="node"> elements)
            const nodes = svg.querySelectorAll('g.node');
            console.log(`Adding manual click handlers to ${nodes.length} nodes in full-screen view`);

            nodes.forEach(node => {
              // Get the id attribute which contains the node identifier
              const fullId = node.getAttribute('id');
              if (fullId) {
                // Extract the nodeId from the id (format: "flowchart-nodeId-randomNumber")
                const match = fullId.match(/flowchart-(.+?)-\d+/);
                if (match && match[1]) {
                  const nodeId = match[1];
                  console.log(`Found node with id: ${nodeId}`);

                  // Make the entire node clickable
                  node.style.cursor = 'pointer';
                  node.addEventListener('click', (e) => {
                    e.stopPropagation();
                    console.log('Manual click handler triggered for node:', nodeId);
                    if (window.handleFoundationClick) {
                      window.handleFoundationClick(nodeId);
                    }
                  });
                }
              }
            });
          }
        }, 100);

        console.log('✓ Full-screen Mermaid diagram rendered.');
      } catch (error) {
        console.error('Error rendering full-screen Mermaid diagram:', error);
      }
    }
  }, [showFullScreenDiagram, answer, diagramView, timelineDiagram]);

  const fetchRecentHistory = async () => {
    try {
      const response = await fetch('/api/qa?limit=10');
      const data = await response.json();

      if (data.success) {
        const formattedHistory = data.history.map(item => ({
          question: item.question,
          answer: item.answer,
          sources: item.sources,
          confidence: item.confidence,
          qaId: item.uuid,
          upvotes: item.upvotes || 0,
          timestamp: new Date(item.created_at).toLocaleString()
        }));
        setHistory(formattedHistory);
      }
    } catch (err) {
      console.error('Failed to fetch Q&A history:', err);
    }
  };

  const fetchMyQuestions = async () => {
    if (!user) {
      setMyQuestions([]);
      return;
    }
    try {
      const response = await fetch('/api/qa?filter=mine&limit=10', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        const formatted = data.history.map(item => ({
          question: item.question,
          answer: item.answer,
          sources: item.sources,
          confidence: item.confidence,
          qaId: item.uuid,
          upvotes: item.upvotes || 0,
          timestamp: new Date(item.created_at).toLocaleString()
        }));
        setMyQuestions(formatted);
      }
    } catch (err) {
      console.error('Failed to fetch my questions:', err);
    }
  };

  const fetchPublicQuestions = async () => {
    try {
      const response = await fetch('/api/qa?filter=public&limit=10');
      const data = await response.json();

      if (data.success) {
        const formatted = data.history.map(item => ({
          question: item.question,
          answer: item.answer,
          sources: item.sources,
          confidence: item.confidence,
          qaId: item.uuid,
          upvotes: item.upvotes || 0,
          timestamp: new Date(item.created_at).toLocaleString()
        }));
        setPublicQuestions(formatted);
      }
    } catch (err) {
      console.error('Failed to fetch public questions:', err);
    }
  };

  // Poll for legal analysis results
  const pollForAnalysis = async (qaId) => {
    try {
      const response = await fetch(`/api/qa/${qaId}/analysis`);
      const data = await response.json();

      if (data.success && data.analysisStatus === 'complete') {
        console.log('✅ Legal analysis complete!');

        // Update answer with analysis results
        setAnswer(prevAnswer => ({
          ...prevAnswer,
          clarityScore: data.clarityScore,
          clarityAnalysis: data.clarityAnalysis,
          legalDiagram: data.legalDiagram,
          foundationMap: data.foundationMap,
          analysisStatus: 'complete'
        }));

        // Stop polling
        stopPollingForAnalysis();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to poll for analysis:', err);
      return false;
    }
  };

  // Start polling for analysis results
  const startPollingForAnalysis = (qaId) => {
    console.log(`🔄 Starting to poll for legal analysis (Q&A ${qaId})...`);

    // Stop any existing polling
    stopPollingForAnalysis();

    // Poll every 3 seconds, max 20 times (60 seconds total)
    let pollCount = 0;
    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      console.log(`Polling attempt ${pollCount}/20...`);

      const complete = await pollForAnalysis(qaId);

      if (complete || pollCount >= 20) {
        stopPollingForAnalysis();
        if (!complete && pollCount >= 20) {
          console.log('⚠️ Polling timed out - analysis may still be processing');
        }
      }
    }, 3000);
  };

  // Stop polling
  const stopPollingForAnalysis = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('🛑 Stopped polling for legal analysis');
    }
  };

  // Progress tracking for question analysis
  const startProgressTracking = () => {
    setProgressStep(0);
    setProgressMessage('Starting analysis...');

    const steps = [
      { percent: 10, message: 'Searching legal database...', delay: 500 },
      { percent: 30, message: 'Retrieving relevant sources...', delay: 1500 },
      { percent: 50, message: 'Generating answer...', delay: 3000 },
      { percent: 70, message: 'Analyzing legal clarity...', delay: 6000 },
      { percent: 90, message: 'Building legal foundations map...', delay: 10000 }
    ];

    steps.forEach((step) => {
      setTimeout(() => {
        if (loading) {
          setProgressStep(step.percent);
          setProgressMessage(step.message);
        }
      }, step.delay);
    });
  };

  const stopProgressTracking = () => {
    setProgressStep(0);
    setProgressMessage('');
  };

  // Cleanup polling and progress on unmount
  useEffect(() => {
    return () => {
      stopPollingForAnalysis();
      stopProgressTracking();
    };
  }, []);

  const loadQAById = async (id) => {
    setLoading(true);
    setError(null);
    setIsDeepLink(true);

    try {
      const response = await fetch(`/api/qa/${id}`);
      const data = await response.json();

      if (data.success) {
        const qa = data.qa;

        // Check if analysis is complete
        const hasAnalysis = qa.clarity_score !== null && qa.legal_diagram !== null;

        // Extract foundationMap from clarityAnalysis if available
        const foundationMap = qa.clarity_analysis?.foundationMap || null;

        setQuestion(qa.question);
        setAnswer({
          question: qa.question,
          answer: qa.answer,
          sources: qa.sources,
          confidence: qa.confidence,
          qaId: qa.uuid,
          upvotes: qa.upvotes || 0,
          timestamp: new Date(qa.created_at).toLocaleString(),
          clarityScore: qa.clarity_score,
          clarityAnalysis: qa.clarity_analysis,
          legalDiagram: qa.legal_diagram,
          foundationMap: foundationMap,
          analysisStatus: hasAnalysis ? 'complete' : 'pending'
        });

        // Load the full thread if this Q&A has a thread_id
        if (qa.thread_id) {
          await loadThread(qa.thread_id);
        }

        // Start polling if analysis is not complete yet
        if (!hasAnalysis) {
          startPollingForAnalysis(qa.uuid);
        }
      } else {
        setError('Q&A not found');
      }
    } catch (err) {
      setError('Failed to load Q&A: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (threadId) => {
    try {
      const response = await fetch(`/api/qa/thread/${threadId}`);
      const data = await response.json();

      if (data.success) {
        const formattedThread = data.thread.map(item => {
          const foundationMap = item.clarity_analysis?.foundationMap || null;
          return {
            question: item.question,
            answer: item.answer,
            sources: item.sources,
            confidence: item.confidence,
            qaId: item.uuid,
            upvotes: item.upvotes || 0,
            timestamp: new Date(item.created_at).toLocaleString(),
            clarityScore: item.clarity_score,
            clarityAnalysis: item.clarity_analysis,
            legalDiagram: item.legal_diagram,
            foundationMap: foundationMap,
            analysisStatus: (item.clarity_score !== null && item.legal_diagram !== null) ? 'complete' : 'pending'
          };
        });
        setThreadQAs(formattedThread);
        setCurrentThreadId(threadId);
      }
    } catch (err) {
      console.error('Failed to load thread:', err);
    }
  };

  const handleHistoryItemClick = (item) => {
    navigate(`/qa/${item.qaId}`);
  };

  const handleAskAnotherQuestion = () => {
    navigate('/qa');
    setIsDeepLink(false);
    setQuestion('');
    setAnswer(null);
    setError(null);
    setFollowUpQuestion('');
    setThreadQAs([]);
    setCurrentThreadId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError(null);
    startProgressTracking();

    try {
      const response = await fetch('/api/rag/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          question: question,
          filters: {},
          topK: 5,
          isPrivate: isPrivate
        }),
      });

      const data = await response.json();

      if (data.success) {
        const result = {
          question: question,
          answer: data.answer,
          sources: data.sources,
          confidence: data.confidence,
          qaId: data.qaId,
          upvotes: 0,
          timestamp: new Date().toLocaleString(),
          autoDiscovery: data.autoDiscovery,
          clarityScore: data.clarityScore,
          clarityAnalysis: data.clarityAnalysis,
          legalDiagram: data.legalDiagram,
          foundationMap: data.foundationMap,
          analysisStatus: data.analysisStatus || 'pending'
        };
        setAnswer(result);

        // Set thread info
        setCurrentThreadId(data.threadId);
        setThreadQAs([result]);

        // Update URL with Q&A ID for deep linking
        navigate(`/qa/${data.qaId}`);

        // Switch to deep link view (hide form, show answer only)
        setIsDeepLink(true);

        // Refresh history from database
        fetchRecentHistory();
        fetchMyQuestions();
        fetchPublicQuestions();

        // Start polling for legal analysis if it's pending
        if (data.analysisStatus === 'pending') {
          startPollingForAnalysis(data.qaId);
        }
      } else {
        setError(data.error || 'Failed to get answer');
      }
    } catch (err) {
      setError('Failed to query RAG system: ' + err.message);
    } finally {
      setLoading(false);
      stopProgressTracking();
    }
  };

  const handleExampleClick = (exampleQuestion) => {
    setQuestion(exampleQuestion);
    navigate('/qa');
    setIsDeepLink(false);
  };

  const copyShareLink = () => {
    if (answer && answer.qaId) {
      const url = `${window.location.origin}/qa/${answer.qaId}`;
      navigator.clipboard.writeText(url);
      alert('Share link copied to clipboard!');
    }
  };

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    if (!followUpQuestion.trim() || !currentThreadId || !answer) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/rag/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: followUpQuestion,
          filters: {},
          topK: 5,
          thread_id: currentThreadId,
          parent_id: answer.qaId
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reload the full thread to get updated conversation
        await loadThread(currentThreadId);

        // Update URL to new Q&A and navigate
        navigate(`/qa/${data.qaId}`);

        // Clear follow-up input
        setFollowUpQuestion('');

        // Refresh history
        fetchRecentHistory();
        fetchMyQuestions();
        fetchPublicQuestions();
      } else {
        setError(data.error || 'Failed to get answer');
      }
    } catch (err) {
      setError('Failed to query RAG system: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async () => {
    if (!answer || !answer.qaId || upvoting) return;

    setUpvoting(true);
    try {
      const response = await fetch(`/api/qa/${answer.qaId}/upvote`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        // Update answer with new upvote count
        setAnswer(prev => ({
          ...prev,
          upvotes: data.upvotes
        }));

        // Refresh history to show updated upvote count
        fetchRecentHistory();
        fetchMyQuestions();
        fetchPublicQuestions();
      } else {
        console.error('Failed to upvote:', data.error);
      }
    } catch (err) {
      console.error('Failed to upvote:', err);
    } finally {
      setUpvoting(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim() || !answer || !answer.qaId || submittingFeedback) return;

    setSubmittingFeedback(true);
    setFeedbackResult(null);

    try {
      const response = await fetch(`/api/qa/${answer.qaId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feedback: feedbackText }),
      });

      const data = await response.json();

      if (data.success) {
        // Check if processing is happening in background
        if (data.processing) {
          // Keep form hidden but don't show result yet - submittingFeedback stays true
          // Clear feedback text
          setFeedbackText('');

          // Poll for updates every 5 seconds
          const pollInterval = setInterval(async () => {
            try {
              const qaResponse = await fetch(`/api/qa/${answer.qaId}`);
              const qaData = await qaResponse.json();

              // Check if there's a new revision
              const revisionsResponse = await fetch(`/api/qa/${answer.qaId}/revisions`);
              const revisionsData = await revisionsResponse.json();

              if (revisionsData.success && revisionsData.revisions && revisionsData.revisions.length > 0) {
                // New revision detected, update the answer
                clearInterval(pollInterval);

                if (qaData.success) {
                  setAnswer(qaData.qa);
                  setFeedbackResult({
                    revised: true,
                    message: 'Answer has been revised based on your feedback!',
                    processing: false
                  });
                  setSubmittingFeedback(false);
                }
              }
            } catch (pollError) {
              console.error('Error polling for updates:', pollError);
            }
          }, 5000);

          // Stop polling after 2 minutes
          setTimeout(() => {
            clearInterval(pollInterval);
            if (!feedbackResult) {
              setFeedbackResult({
                revised: false,
                message: 'Processing is taking longer than expected. Please refresh the page to see updates.',
                processing: false
              });
              setSubmittingFeedback(false);
            }
          }, 120000);

        } else {
          // Old synchronous response format (backward compatibility)
          setFeedbackResult({
            revised: data.revised,
            message: data.message,
            revisedAnswer: data.revisedAnswer
          });

          if (data.revised && data.revisedAnswer) {
            setAnswer(prev => ({
              ...prev,
              answer: data.revisedAnswer
            }));
          }

          setFeedbackText('');
          setSubmittingFeedback(false);
        }
      } else {
        setFeedbackResult({
          revised: false,
          message: 'Failed to process feedback: ' + data.error
        });
        setSubmittingFeedback(false);
      }
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setFeedbackResult({
        revised: false,
        message: 'Failed to submit feedback: ' + err.message
      });
      setSubmittingFeedback(false);
    }
  };

  const handleViewRevisions = async () => {
    if (!answer || !answer.qaId) return;

    setLoadingRevisions(true);
    setShowRevisionModal(true);

    try {
      const response = await fetch(`/api/qa/${answer.qaId}/revisions`);
      const data = await response.json();

      if (data.success) {
        setRevisions(data.revisions || []);
      } else {
        console.error('Failed to fetch revisions:', data.error);
        setRevisions([]);
      }
    } catch (err) {
      console.error('Failed to fetch revisions:', err);
      setRevisions([]);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleReprocessAnalysis = async () => {
    if (!answer || !answer.qaId || reprocessing) return;

    if (!window.confirm('Are you sure you want to reprocess the legal analysis and mermaid diagram? This will regenerate the analysis from scratch.')) {
      return;
    }

    setReprocessing(true);
    try {
      const response = await fetch(`/api/qa/${answer.qaId}/reprocess`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        alert('Analysis reprocessing started. The page will refresh in 5 seconds to show the new analysis.');

        // Wait 5 seconds then reload the page to show updated analysis
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } else {
        alert('Failed to reprocess analysis: ' + data.error);
      }
    } catch (err) {
      console.error('Failed to reprocess analysis:', err);
      alert('Failed to reprocess analysis: ' + err.message);
    } finally {
      setReprocessing(false);
    }
  };

  const handleReprocessDiagram = async () => {
    if (!answer || !answer.qaId || reprocessingDiagram) return;

    if (!window.confirm('Are you sure you want to regenerate the Legal Foundations Map diagram? This will apply any recent updates (like year display) to this answer.')) {
      return;
    }

    setReprocessingDiagram(true);
    try {
      const response = await fetch(`/api/qa/${answer.qaId}/reprocess-diagram`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        alert('Diagram regenerated successfully. The page will refresh to show the updated diagram.');
        window.location.reload();
      } else {
        alert('Failed to regenerate diagram: ' + data.error);
      }
    } catch (err) {
      console.error('Failed to regenerate diagram:', err);
      alert('Failed to regenerate diagram: ' + err.message);
    } finally {
      setReprocessingDiagram(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!answer || !answer.qaId || deleting) return;

    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`/api/qa/${answer.qaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        alert('Question deleted successfully');
        // Redirect to home
        window.location.href = '/';
      } else {
        alert('Failed to delete question: ' + data.error);
      }
    } catch (err) {
      console.error('Failed to delete question:', err);
      alert('Failed to delete question: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const exampleQuestions = [
    "What are the key provisions of the Americans with Disabilities Act?",
    "What protections does the Fair Housing Act provide?",
    "Explain the criminal penalties in Title 18 U.S.C.",
    "What are the requirements of the Clean Air Act?",
    "How does the Sherman Act address antitrust violations?",
    "What rights are protected by the Bill of Rights?",
    "What are the employment protections under FLSA?"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Legal Q&A</h1>
          <p className="text-gray-600 mt-2">Research questions about U.S. federal law and get AI-generated information with source citations</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LegalDisclaimer variant="banner" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Query Form (hidden when viewing deep link) */}
            {!isDeepLink && (
              <>
                <div className="card">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="label">Your Question</label>
                      <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="Ask any question about U.S. federal law..."
                        rows="4"
                        disabled={loading}
                        className="textarea"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading || !question.trim()}
                        className="btn btn-primary px-8"
                      >
                        {loading ? 'Analyzing...' : 'Get Answer'}
                      </button>
                    </div>

                    {user && (
                      <div className="flex items-center space-x-2 pt-2">
                        <input
                          type="checkbox"
                          id="isPrivate"
                          checked={isPrivate}
                          onChange={(e) => setIsPrivate(e.target.checked)}
                          disabled={loading}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isPrivate" className="text-sm text-gray-700 cursor-pointer">
                          Make this question private (only visible to you)
                        </label>
                      </div>
                    )}
                  </form>
                </div>

                {/* Example Questions */}
                <div className="card bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Example Questions:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {exampleQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleExampleClick(q)}
                        disabled={loading}
                        className="btn btn-outline text-left text-sm py-2"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      <strong>Error:</strong> {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Progress Bar */}
            {loading && !answer && (
              <div className="card">
                <div className="text-center mb-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-700 font-medium">Analyzing your legal question...</p>
                </div>

                {/* Progress Bar */}
                <div className="max-w-2xl mx-auto">
                  <div className="mb-2 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      {progressMessage || 'Starting analysis...'}
                    </span>
                    <span className="text-sm font-semibold text-primary-600">
                      {progressStep}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-primary-600 h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progressStep}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    This typically takes 10-30 seconds
                  </p>
                </div>
              </div>
            )}

            {/* Answer Display */}
            {answer && (
              <div className="space-y-6">
                {/* Show question when viewing deep link */}
                {isDeepLink && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Question:</h2>
                    <p className="text-gray-800 leading-relaxed">{answer.question}</p>
                  </div>
                )}

                <div className="card">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Answer</h2>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Upvote Button */}
                      <button
                        onClick={handleUpvote}
                        disabled={upvoting}
                        className="flex items-center gap-2 px-3 py-2 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-blue-50 transition-all disabled:opacity-50"
                      >
                        <span className="text-xl text-primary-600">▲</span>
                        <span className="font-semibold text-gray-900">{answer.upvotes || 0}</span>
                      </button>

                      {/* Confidence Badge */}
                      <span className="badge badge-green text-sm px-3 py-1.5">
                        Confidence: {(answer.confidence * 100).toFixed(0)}%
                      </span>

                      {/* Legal Clarity Badge */}
                      {answer.clarityScore && (
                        <span className={`text-sm px-3 py-1.5 rounded-lg font-semibold ${
                          answer.clarityScore >= 8 ? 'bg-green-100 text-green-800' :
                          answer.clarityScore >= 5 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          Legal Clarity: {answer.clarityScore}/10
                        </span>
                      )}

                      {/* Share Button */}
                      {answer.qaId && (
                        <button onClick={copyShareLink} className="btn btn-secondary text-sm flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Share
                        </button>
                      )}

                      {/* Provide Feedback Button */}
                      {answer.qaId && (
                        <button onClick={() => setShowFeedbackModal(true)} className="btn btn-primary text-sm flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                          Provide Feedback
                        </button>
                      )}

                      {/* View Revision History Button */}
                      {answer.qaId && (
                        <button onClick={handleViewRevisions} className="btn btn-secondary text-sm flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Revision History
                        </button>
                      )}

                      {/* Admin: Reprocess Analysis Button */}
                      {isAdmin && answer.qaId && (
                        <button
                          onClick={handleReprocessAnalysis}
                          disabled={reprocessing}
                          className="btn bg-purple-600 hover:bg-purple-700 text-white text-sm flex items-center gap-1 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          {reprocessing ? 'Reprocessing...' : 'Reprocess Analysis'}
                        </button>
                      )}

                      {/* Admin: Delete Question Button */}
                      {isAdmin && answer.qaId && (
                        <button
                          onClick={handleDeleteQuestion}
                          disabled={deleting}
                          className="btn bg-red-600 hover:bg-red-700 text-white text-sm flex items-center gap-1 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {deleting ? 'Deleting...' : 'Delete Question'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Markdown Answer */}
                  <div className="prose prose-blue max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-4" {...props} />,
                        h2: ({node, ...props}) => <h2 className="text-xl font-bold text-gray-900 mt-5 mb-3" {...props} />,
                        h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2" {...props} />,
                        p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                        li: ({node, ...props}) => <li className="leading-relaxed" {...props} />,
                        strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                        code: ({node, inline, ...props}) =>
                          inline ?
                            <code className="bg-blue-50 text-primary-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props} /> :
                            <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono" {...props} />,
                        blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-600 my-4" {...props} />,
                        a: ({node, ...props}) => <a className="text-primary-600 hover:text-primary-700 underline" {...props} />,
                      }}
                    >
                      {answer.answer}
                    </ReactMarkdown>
                  </div>

                  {/* Follow-up Question */}
                  {isDeepLink && currentThreadId && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <form onSubmit={handleFollowUpSubmit} className="space-y-3">
                        <label className="label text-base">Ask a Follow-up Question:</label>
                        <div className="flex gap-3">
                          <input
                            type="text"
                            value={followUpQuestion}
                            onChange={(e) => setFollowUpQuestion(e.target.value)}
                            placeholder="Ask a follow-up question..."
                            disabled={loading}
                            className="input flex-1"
                          />
                          <button
                            type="submit"
                            disabled={loading || !followUpQuestion.trim()}
                            className="btn btn-primary whitespace-nowrap"
                          >
                            {loading ? 'Asking...' : 'Ask'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Thread History */}
                  {threadQAs.length > 1 && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation Thread:</h3>
                      <div className="space-y-4">
                        {threadQAs.slice(0, -1).map((qa, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="font-medium text-gray-900 mb-2">Q: {qa.question}</div>
                            <div className="text-sm text-gray-700 line-clamp-3">A: {qa.answer}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legal Clarity Analysis */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Legal Clarity Analysis</h3>
                    {answer.analysisStatus === 'pending' ? (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                        <div className="flex items-center gap-3">
                          <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <div>
                            <div className="text-base font-semibold text-blue-900">Analyzing Legal Clarity...</div>
                            <div className="text-sm text-blue-700">This may take 30-60 seconds. The analysis will appear automatically when complete.</div>
                          </div>
                        </div>
                      </div>
                    ) : answer.clarityAnalysis ? (
                      <div className={`border-l-4 p-4 rounded-lg ${
                        answer.clarityScore >= 8 ? 'bg-green-50 border-green-500' :
                        answer.clarityScore >= 5 ? 'bg-yellow-50 border-yellow-500' :
                        'bg-red-50 border-red-500'
                      }`}>
                        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-300">
                          <div className="text-3xl font-bold">
                            <span className={
                              answer.clarityScore >= 8 ? 'text-green-700' :
                              answer.clarityScore >= 5 ? 'text-yellow-700' :
                              'text-red-700'
                            }>{answer.clarityScore}</span>
                            <span className="text-gray-500 text-xl">/10</span>
                          </div>
                          <div className="text-sm font-semibold text-gray-700">
                            {answer.clarityScore >= 8 ? 'High Legal Clarity' :
                             answer.clarityScore >= 5 ? 'Moderate Legal Clarity' :
                             'Low Legal Clarity'}
                          </div>
                        </div>
                        <div className="text-sm text-gray-800 mb-3">
                          <strong>Reasoning:</strong> {answer.clarityAnalysis.reasoning}
                        </div>
                        {answer.clarityAnalysis.overallAssessment && (
                          <div className="text-sm text-gray-700 mb-3">
                            {answer.clarityAnalysis.overallAssessment}
                          </div>
                        )}
                        {answer.clarityAnalysis.contradictions && answer.clarityAnalysis.contradictions.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-semibold text-red-800 mb-1">Contradictions:</div>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                              {answer.clarityAnalysis.contradictions.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {answer.clarityAnalysis.ambiguities && answer.clarityAnalysis.ambiguities.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-semibold text-yellow-800 mb-1">Ambiguities:</div>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                              {answer.clarityAnalysis.ambiguities.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {answer.clarityAnalysis.jurisdictionIssues && answer.clarityAnalysis.jurisdictionIssues.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-semibold text-blue-800 mb-1">Jurisdiction Issues:</div>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                              {answer.clarityAnalysis.jurisdictionIssues.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Legal Foundations Diagram */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Legal Foundations Map</h3>
                      <div className="flex flex-wrap gap-2 items-center">
                        {answer.legalDiagram && answer.foundationMap && (
                          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 w-full sm:w-auto">
                            <button
                              onClick={() => setDiagramView('hierarchy')}
                              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                diagramView === 'hierarchy'
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-700 hover:text-gray-900'
                              }`}
                            >
                              Hierarchy
                            </button>
                            <button
                              onClick={() => setDiagramView('timeline')}
                              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                diagramView === 'timeline'
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-700 hover:text-gray-900'
                              }`}
                            >
                              Timeline
                            </button>
                          </div>
                        )}
                        {answer.legalDiagram && (
                          <button
                            onClick={() => setShowFullScreenDiagram(true)}
                            className="btn bg-gray-700 hover:bg-gray-800 text-white text-sm px-4 py-2 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2 w-full sm:w-auto"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            <span className="hidden sm:inline">Full Screen</span>
                            <span className="sm:hidden">Fullscreen</span>
                          </button>
                        )}
                        {isAdmin && answer.qaId && answer.legalDiagram && (
                          <button
                            onClick={handleReprocessDiagram}
                            disabled={reprocessingDiagram}
                            className="btn bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 w-full sm:w-auto"
                          >
                            {reprocessingDiagram ? 'Regenerating...' : 'Regenerate Diagram'}
                          </button>
                        )}
                      </div>
                    </div>
                    {answer.analysisStatus === 'pending' ? (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                        <div className="flex items-center gap-3">
                          <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <div>
                            <div className="text-base font-semibold text-blue-900">Building Legal Foundations Map...</div>
                            <div className="text-sm text-blue-700">The diagram will appear automatically when analysis is complete.</div>
                          </div>
                        </div>
                      </div>
                    ) : answer.legalDiagram ? (
                      <>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 overflow-x-auto" style={{ minHeight: '600px' }}>
                          <div ref={mermaidRef} className="mermaid" style={{ fontSize: '16px', width: '100%' }}></div>
                        </div>
                      <div className="mt-3 text-xs text-gray-600 space-y-1">
                        <p>This diagram shows the hierarchy of legal authorities flowing from constitutional foundations down through statutes, regulations, and case law.</p>
                        <p>★ indicates primary legal authorities</p>
                        <p className="text-primary-600 font-semibold">💡 Click on any box to view the actual legal text and details</p>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* Auto-Discovery Notification */}
                  {answer.autoDiscovery && answer.autoDiscovery.queuedCount > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-green-900 mb-2">
                              New Legal Sources Discovered
                            </h4>
                            <p className="text-sm text-green-800 mb-3">
                              Found {answer.autoDiscovery.queuedCount} new legal {answer.autoDiscovery.queuedCount === 1 ? 'source' : 'sources'} mentioned in this answer that have been queued for processing:
                            </p>
                            <ul className="space-y-2">
                              {answer.autoDiscovery.queuedSources.map((source) => (
                                <li key={source.id} className="bg-white rounded px-3 py-2 border border-green-200">
                                  <div className="text-sm font-medium text-gray-900">{source.name}</div>
                                  {source.short_name && (
                                    <div className="text-xs text-gray-600 mt-1">{source.short_name}</div>
                                  )}
                                  <div className="flex gap-2 mt-1">
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                      {source.type}
                                    </span>
                                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                                      {source.category}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                            <p className="text-xs text-green-700 mt-3">
                              These sources will be collected and added to the database for future queries. Visit the Source Manager to track their status.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sources */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Sources Cited:</h3>
                    <div className="space-y-3">
                      {answer.sources.map((source, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 bg-primary-600 text-white text-xs font-bold px-2 py-1 rounded">
                              [{idx + 1}]
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <strong className="text-gray-900 font-semibold">{source.name || source.shortName}</strong>
                                <span className="badge badge-blue flex-shrink-0">
                                  {(source.similarity * 100).toFixed(0)}% relevant
                                </span>
                              </div>
                              {source.section && (
                                <div className="text-sm text-gray-600 mb-1">Section: {source.section}</div>
                              )}
                              {source.category && (
                                <div className="text-xs text-gray-500 mb-2">{source.category}</div>
                              )}
                              {source.url && (
                                <a
                                  href={source.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-700 text-sm font-medium inline-flex items-center gap-1"
                                >
                                  View Source
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card sticky top-4 space-y-4">
              {/* Ask Another Question Button */}
              {isDeepLink && answer && (
                <button onClick={handleAskAnotherQuestion} className="btn btn-purple w-full">
                  Ask Another Question
                </button>
              )}

              {/* My questions section */}
              {user && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">My questions</h3>
                  {myQuestions.length === 0 ? (
                    <p className="text-gray-500 text-center py-4 text-sm italic">No questions yet</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {myQuestions.map((item, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleHistoryItemClick(item)}
                          className="p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-primary-300 transition-all"
                        >
                          <div className="text-sm text-gray-900 leading-snug mb-2 line-clamp-3">
                            {item.question}
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-primary-600 font-semibold">▲ {item.upvotes || 0}</span>
                            <span className="text-gray-500">{item.timestamp}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Public questions section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Public questions</h3>
                {publicQuestions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4 text-sm italic">No questions yet</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {publicQuestions.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleHistoryItemClick(item)}
                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-primary-300 transition-all"
                      >
                        <div className="text-sm text-gray-900 leading-snug mb-2 line-clamp-3">
                          {item.question}
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary-600 font-semibold">▲ {item.upvotes || 0}</span>
                          <span className="text-gray-500">{item.timestamp}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Foundation Detail Modal */}
      {showFoundationModal && selectedFoundation && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowFoundationModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedFoundation.name}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      {selectedFoundation.citation}
                    </span>
                    <span className={`text-sm px-3 py-1 rounded-full font-semibold ${
                      selectedFoundation.level === 'constitutional' ? 'bg-green-100 text-green-800' :
                      selectedFoundation.level === 'statutory' ? 'bg-blue-100 text-blue-800' :
                      selectedFoundation.level === 'regulatory' ? 'bg-orange-100 text-orange-800' :
                      selectedFoundation.level === 'case_law' ? 'bg-purple-100 text-purple-800' :
                      'bg-pink-100 text-pink-800'
                    }`}>
                      {selectedFoundation.level.replace('_', ' ').toUpperCase()}
                    </span>
                    {selectedFoundation.relevance === 'primary' && (
                      <span className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold">
                        ★ PRIMARY AUTHORITY
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowFoundationModal(false)}
                  className="text-gray-400 hover:text-gray-600 ml-4"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6 space-y-6">
              {/* Summary */}
              {selectedFoundation.summary && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">How This Applies</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedFoundation.summary}
                  </p>
                </div>
              )}

              {/* Legal Text Excerpt */}
              {selectedFoundation.textExcerpt && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal Text</h3>
                  <div className="bg-gray-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-gray-800 leading-relaxed italic">
                      "{selectedFoundation.textExcerpt}"
                    </p>
                  </div>
                </div>
              )}

              {/* Category */}
              {selectedFoundation.category && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Category</h3>
                  <span className="inline-block text-sm bg-gray-200 text-gray-800 px-3 py-1 rounded">
                    {selectedFoundation.category.replace('_', ' ')}
                  </span>
                </div>
              )}

              {/* Relationships */}
              {selectedFoundation.relationships && selectedFoundation.relationships.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Legal Relationships</h3>
                  <div className="space-y-2">
                    {selectedFoundation.relationships.map((rel, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-gray-700 capitalize">{rel.type}:</span>
                        <span className="text-gray-600">{rel.targetId}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowFoundationModal(false)}
                className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowFeedbackModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Provide Feedback
                  </h2>
                  <p className="text-sm text-gray-600">
                    Share your concerns or questions about this answer. We'll research and revise if necessary.
                  </p>
                </div>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="text-gray-400 hover:text-gray-600 ml-4"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6 space-y-4">
              {submittingFeedback && !feedbackResult ? (
                // Show loading animation while processing
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative w-20 h-20 mb-6">
                    <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Researching Your Feedback
                  </h3>
                  <p className="text-sm text-gray-600 text-center max-w-md">
                    We're analyzing legal sources and determining if revisions are needed. This typically takes 30-60 seconds.
                  </p>
                </div>
              ) : !feedbackResult ? (
                // Show feedback form when no result yet
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Your Feedback or Question
                    </label>
                    <textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="Example: I think this answer is incomplete because it doesn't address how this applies to minors..."
                      rows="6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={submittingFeedback}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Note: We'll research your feedback using legal sources. Only evidence-backed revisions will be made.
                    </p>
                  </div>
                </>
              ) : (
                // Show only result after processing
                <div className={`border-l-4 p-4 rounded-lg ${
                  feedbackResult.revised ? 'bg-green-50 border-green-500' : 'bg-blue-50 border-blue-500'
                }`}>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {feedbackResult.revised ? '✓ Answer Revised' : 'ℹ No Revision Needed'}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {feedbackResult.message}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
              {submittingFeedback && !feedbackResult ? (
                // Hide buttons while processing
                <div className="w-full h-14"></div>
              ) : !feedbackResult ? (
                // Show Cancel and Submit buttons before processing
                <>
                  <button
                    onClick={() => {
                      setShowFeedbackModal(false);
                      setFeedbackText('');
                      setFeedbackResult(null);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                    disabled={submittingFeedback}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitFeedback}
                    className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                    disabled={submittingFeedback || !feedbackText.trim()}
                  >
                    {submittingFeedback ? 'Researching...' : 'Submit Feedback'}
                  </button>
                </>
              ) : (
                // Show only OK button after processing
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setFeedbackText('');
                    setFeedbackResult(null);
                  }}
                  className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Diagram Modal */}
      {showFullScreenDiagram && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
          <div className="w-full h-full flex flex-col">
            {/* Modal Header */}
            <div className="bg-gray-900 border-b border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-bold text-white">
                    Legal Foundations Map - {diagramView === 'hierarchy' ? 'Hierarchy View' : 'Timeline View'}
                  </h2>
                  {answer.legalDiagram && answer.foundationMap && (
                    <div className="inline-flex rounded-lg border border-gray-600 bg-gray-800 p-1">
                      <button
                        onClick={() => setDiagramView('hierarchy')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          diagramView === 'hierarchy'
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        Hierarchy
                      </button>
                      <button
                        onClick={() => setDiagramView('timeline')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          diagramView === 'timeline'
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        Timeline
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowFullScreenDiagram(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto bg-white p-8">
              <div ref={fullScreenMermaidRef} className="mermaid w-full" style={{ fontSize: '16px' }}></div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-900 border-t border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  <span className="text-primary-400 font-semibold">💡 Click on any box to view details</span>
                  <span className="mx-2">|</span>
                  <span>★ indicates primary legal authorities</span>
                </p>
                <button
                  onClick={() => setShowFullScreenDiagram(false)}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  Exit Full Screen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revision History Modal */}
      {showRevisionModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRevisionModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Revision History
                  </h2>
                  <p className="text-sm text-gray-600">
                    View all revisions made to this answer based on user feedback
                  </p>
                </div>
                <button
                  onClick={() => setShowRevisionModal(false)}
                  className="text-gray-400 hover:text-gray-600 ml-4"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6">
              {loadingRevisions ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : revisions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No revisions yet. This answer hasn't been revised based on feedback.
                </div>
              ) : (
                <div className="space-y-6">
                  {revisions.map((revision, index) => (
                    <div key={revision.id} className="border-l-4 border-primary-500 pl-4 py-2">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">
                          Revision #{revisions.length - index}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {new Date(revision.created_at).toLocaleString()}
                        </span>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-700 mb-1">User Feedback:</p>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                          {revision.feedback}
                        </p>
                      </div>

                      {revision.research_findings && revision.research_findings.validConcerns && (
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Valid Concerns Found:</p>
                          <ul className="text-sm text-gray-600 list-disc list-inside bg-green-50 p-3 rounded">
                            {revision.research_findings.validConcerns.map((concern, i) => (
                              <li key={i}>{concern}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Legal Clarity Score Changes */}
                      {(revision.old_clarity_score !== null || revision.new_clarity_score !== null) && (
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Legal Clarity Score:</p>
                          <div className="flex items-center gap-3 bg-blue-50 p-3 rounded">
                            <div className="flex-1">
                              <span className="text-xs text-gray-500">Before:</span>
                              <div className="text-lg font-bold text-gray-900">
                                {revision.old_clarity_score ? parseFloat(revision.old_clarity_score).toFixed(1) : 'N/A'} / 10
                              </div>
                            </div>
                            <div className="text-2xl text-gray-400">→</div>
                            <div className="flex-1">
                              <span className="text-xs text-gray-500">After:</span>
                              <div className={`text-lg font-bold ${
                                parseFloat(revision.new_clarity_score) > parseFloat(revision.old_clarity_score) ? 'text-green-600' :
                                parseFloat(revision.new_clarity_score) < parseFloat(revision.old_clarity_score) ? 'text-red-600' :
                                'text-gray-900'
                              }`}>
                                {revision.new_clarity_score ? parseFloat(revision.new_clarity_score).toFixed(1) : 'N/A'} / 10
                              </div>
                            </div>
                            {revision.new_clarity_score && revision.old_clarity_score && (
                              <div className={`text-sm font-semibold px-2 py-1 rounded ${
                                parseFloat(revision.new_clarity_score) > parseFloat(revision.old_clarity_score) ? 'bg-green-100 text-green-700' :
                                parseFloat(revision.new_clarity_score) < parseFloat(revision.old_clarity_score) ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {parseFloat(revision.new_clarity_score) > parseFloat(revision.old_clarity_score) ? '+' : ''}
                                {(parseFloat(revision.new_clarity_score) - parseFloat(revision.old_clarity_score)).toFixed(1)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Legal Foundations Diagram Changes */}
                      {(revision.old_legal_diagram || revision.new_legal_diagram) && (
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Legal Foundations Mapping:</p>
                          <div className="bg-purple-50 p-3 rounded">
                            <p className="text-xs text-gray-600">
                              {revision.old_legal_diagram && revision.new_legal_diagram ?
                                'Legal foundations diagram was regenerated for the revised answer' :
                                revision.new_legal_diagram ?
                                'Legal foundations diagram was added' :
                                'Legal foundations diagram was removed'}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">Old Answer:</p>
                          <div className="text-sm text-gray-600 bg-red-50 p-3 rounded max-h-40 overflow-y-auto">
                            {revision.old_answer.substring(0, 300)}...
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">New Answer:</p>
                          <div className="text-sm text-gray-600 bg-green-50 p-3 rounded max-h-40 overflow-y-auto">
                            {revision.new_answer.substring(0, 300)}...
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowRevisionModal(false)}
                className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LegalQA;
